import Component from '@glimmer/component';
import { LinkTo } from '@ember/routing';
import { t } from 'ember-intl';
import { PhClockCounterClockwise } from 'ember-phosphor-icons';
import Chip, { type ChipIntent } from 'tio-ui/components/chip';
import { formatShortDate } from 'tio-ui/format';
import BenefitCard from 'tio-employee/components/dashboard-kit/benefit-card';
import type {
  DecisionOutcome,
  RecentDecision,
} from 'tio-employee/utils/partner-dashboard/sources/recent-decisions';
import type { WidgetComponentSignature } from 'tio-employee/utils/partner-dashboard/registry';

const OUTCOME_INTENTS: Record<DecisionOutcome, ChipIntent> = {
  approve: 'success',
  reject: 'danger',
  decline: 'danger',
  request_change: 'warning',
};

/** Pertinent card: the viewer's latest approval decisions, each linking back to the record. */
export default class RecentDecisionsWidget extends Component<
  WidgetComponentSignature<RecentDecision[]>
> {
  outcomeIntent = (outcome: DecisionOutcome) => OUTCOME_INTENTS[outcome];

  outcomeKey = (outcome: DecisionOutcome) => `partner_dashboard.decisions.outcome_${outcome}`;

  <template>
    <BenefitCard
      @title={{t "partner_dashboard.decisions.title"}}
      @icon={{PhClockCounterClockwise}}
      @actionLabel={{t "partner_dashboard.queue.open_queue"}}
      @actionRoute="authenticated.admin.tas.approval-queue"
      data-test-partner-dashboard-recent-decisions
    >
      {{#if @data.length}}
        <ul class="tio-col gap-3 pii" role="list">
          {{#each @data key="id" as |decision|}}
            <li class="tio-row-between gap-3" data-test-partner-dashboard-decision={{decision.id}}>
              <div class="tio-col-extra-tight tio-fill-min">
                {{#if decision.route}}
                  <LinkTo
                    @route={{decision.route}}
                    @model={{decision.model}}
                    class="tio-text-base-semibold app-text-link"
                  >{{decision.employeeName}}</LinkTo>
                {{else}}
                  <span class="tio-text-base-semibold">{{decision.employeeName}}</span>
                {{/if}}
                <span class="tio-row-tight flex-wrap tio-text-sm-medium tio-fg-secondary">
                  <span>{{decision.programName}}</span>
                  {{#if decision.decidedAt}}
                    <time datetime={{decision.decidedAt}}>{{formatShortDate
                        decision.decidedAt
                      }}</time>
                  {{/if}}
                </span>
              </div>
              <Chip @intent={{this.outcomeIntent decision.outcome}} @appearance="faded" @size="sm">
                {{t (this.outcomeKey decision.outcome)}}
              </Chip>
            </li>
          {{/each}}
        </ul>
      {{else}}
        <p class="tio-text-sm-medium tio-fg-secondary">{{t "partner_dashboard.decisions.empty"}}</p>
      {{/if}}
    </BenefitCard>
  </template>
}
